/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Box } from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { PCView, SPView } from "components/common/MediaQuery";
import { useDisclosure } from "hooks/useDisclosure";
import React from "react";
import { useSelector } from "store";
import { StreamingIconButton } from "ui/StreamingIconButton";

import { AnnouncementIcon } from "./AnnouncementIcon";
import { VoteIcon } from "./VoteIcon";
import {
  isPopOut,
  POP_OUT_QUERY_PARAM_KEY,
  POP_OUT_QUERY_PARAM_VALUE,
} from "./enableCommentPopOut";

import type { SerializedStyles } from "@emotion/react";
import type { CommentFilterType } from "modules/streaming";
import type { FC, SVGAttributes } from "react";

type CommentTypesRecord = {
  type: CommentFilterType;
  label: string;
};

const COLOR_FFFFFF = "#fff";
const COLOR_000000 = "#000";
const COLOR_FFFFFF_090 = "rgba(255, 255, 255, 0.9)";
const COLOR_000000_080 = "rgba(0, 0, 0, 0.8)";
const COLOR_FFFFFF_HOVER = "rgba(255, 255, 255, 0.2)";
const COLOR_000000_HOVER = "rgba(0, 0, 0, 0.2)";
const COLOR_TRANSPARENT = "transparent";

const getCommentFilterItemStyle = (
  input: Record<"isDarkModeEnabled" | "isSelected", boolean>
): SerializedStyles => {
  const { isDarkModeEnabled, isSelected } = input;

  if (isDarkModeEnabled) {
    return css`
      background-color: ${isSelected ? COLOR_000000_080 : COLOR_TRANSPARENT};
      color: ${isSelected ? COLOR_FFFFFF : COLOR_000000};
      &:hover {
        background-color: ${isSelected ? COLOR_000000_080 : COLOR_000000_HOVER};
      }
    `;
  }

  return css`
    background-color: ${isSelected ? COLOR_FFFFFF_090 : COLOR_TRANSPARENT};
    color: ${isSelected ? COLOR_000000 : COLOR_FFFFFF};
    &:hover {
      background-color: ${isSelected ? COLOR_FFFFFF_090 : COLOR_FFFFFF_HOVER};
    }
  `;
};

const COMMENT_TYPES: CommentTypesRecord[] = [
  {
    type: "all",
    label: "コメントとギフト",
  },
  {
    type: "comment",
    label: "コメントのみ",
  },
  {
    type: "gift",
    label: "ギフトのみ",
  },
  {
    type: "none",
    label: "コメント非表示",
  },
];

export const StreamingActionHeader: FC<{
  isDarkModeEnabled: boolean;
  selectedCommentFilter: CommentFilterType;
  showAnnouncement: boolean;
  isLiveEventEnabled: boolean;
  handleChangeCommentFilter: (state: CommentFilterType) => void;
  handleToggleAnnouncement: () => void;
  handleCloseActionArea?: () => void;
  handleToggleLiveEvent: () => void;
}> = ({
  isDarkModeEnabled,
  handleChangeCommentFilter,
  handleToggleAnnouncement,
  handleCloseActionArea,
  selectedCommentFilter,
  showAnnouncement,
  isLiveEventEnabled,
  handleToggleLiveEvent,
}) => {
  const { isOpen: isFilterMenuOpen, onToggle } = useDisclosure();

  const streamingType = useSelector((state) => state.streaming.streamingType);

  const toggleFilterMenu = () => {
    onToggle();
  };

  const handleCommentFilterChange = (filter: CommentFilterType) => {
    handleChangeCommentFilter(filter);
    onToggle();
  };

  return (
    <Box
      display={"flex"}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Box position={"relative"}>
        <StreamingIconButton
          isDarkModeEnabled={isDarkModeEnabled}
          isBgColorEnabled={false}
          onClick={toggleFilterMenu}
        >
          <PCView>
            <FilterIcon
              style={{
                width: "18px",
                height: "auto",
              }}
            />
          </PCView>

          <SPView>
            <SpFilterIcon
              style={{
                width: "18px",
                height: "auto",
              }}
            />
          </SPView>
        </StreamingIconButton>

        {isFilterMenuOpen && (
          <ClickAwayListener onClickAway={onToggle}>
            <Box
              position={"absolute"}
              top={"42px"}
              left={"0"}
              display={"grid"}
              gridTemplateColumns={"1fr"}
              width={"160px"}
              paddingY={"16px"}
              color={isDarkModeEnabled ? "#000" : "#fff"}
              fontSize={"14px"}
              bgcolor={
                isDarkModeEnabled
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(0, 0, 0, 0.8)"
              }
              borderRadius={"8px"}
              zIndex={100}
            >
              {COMMENT_TYPES.map((commentType) => {
                const { label, type } = commentType;

                return (
                  <Box
                    key={type}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleCommentFilterChange(type)}
                    display={"flex"}
                    padding={"4px 16px"}
                    css={getCommentFilterItemStyle({
                      isDarkModeEnabled,
                      isSelected: type === selectedCommentFilter,
                    })}
                  >
                    {label}
                  </Box>
                );
              })}
              {/* ポップアウトウィンドウではポップアウトメニューを非表示にする */}
              {!isPopOut() && (
                <Box
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    // 別タブでコメントページを開く
                    const commentPageUrl = new URL(window.location.href);
                    commentPageUrl.searchParams.set(
                      POP_OUT_QUERY_PARAM_KEY,
                      POP_OUT_QUERY_PARAM_VALUE
                    );
                    window.open(
                      commentPageUrl.toString(),
                      "_blank",
                      "popup,width=400,height=600,noopener"
                    );
                    handleCommentFilterChange("none");
                  }}
                  display={"flex"}
                  padding={"4px 16px"}
                  css={getCommentFilterItemStyle({
                    isDarkModeEnabled,
                    isSelected: false,
                  })}
                >
                  コメントページ
                  <NavigationIcon width={"20px"} height={"20px"} />
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        )}
      </Box>

      <Box display={"flex"} alignItems={"center"}>
        <Box display={"flex"} alignItems={"center"} gridGap={"8px"}>
          {isLiveEventEnabled && (
            <PCView>
              <StreamingIconButton
                isDarkModeEnabled={isDarkModeEnabled}
                onClick={handleToggleLiveEvent}
              >
                <VoteIcon width={"16px"} height={"auto"} />
              </StreamingIconButton>
            </PCView>
          )}

          {/* @todo 既存実装ではイベントタブでアナウンスボタンを使用できないないので、一旦グレーアウトする。
        配信イベントのUIを実装時に解除する */}
          {showAnnouncement && (
            <StreamingIconButton
              disabled={streamingType !== "Live"}
              isDarkModeEnabled={isDarkModeEnabled}
              onClick={handleToggleAnnouncement}
            >
              <AnnouncementIcon
                style={{
                  width: "18px",
                  height: "auto",
                }}
              />
            </StreamingIconButton>
          )}
        </Box>

        {/* TODO:閉じるボタン作る */}
        {handleCloseActionArea && !isPopOut() && (
          <StreamingIconButton
            isDarkModeEnabled={isDarkModeEnabled}
            isBgColorEnabled={false}
            onClick={() => {
              handleCloseActionArea();
            }}
          >
            <CloseIcon
              style={{
                width: "18px",
                height: "auto",
              }}
            />
          </StreamingIconButton>
        )}
      </Box>
    </Box>
  );
};

const FilterIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 20 20" {...props}>
      <path d="M2.5 14.1667V15.8333H7.5V14.1667H2.5ZM2.5 4.16667V5.83333H10.8333V4.16667H2.5ZM10.8333 17.5V15.8333H17.5V14.1667H10.8333V12.5H9.16667V17.5H10.8333ZM5.83333 7.5V9.16667H2.5V10.8333H5.83333V12.5H7.5V7.5H5.83333ZM17.5 10.8333V9.16667H9.16667V10.8333H17.5ZM12.5 7.5H14.1667V5.83333H17.5V4.16667H14.1667V2.5H12.5V7.5Z" />
    </svg>
  );
};

const SpFilterIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 20 20" {...props}>
      <path d="M3.54232 4.71634C3.62565 4.82467 8.32565 10.8247 8.32565 10.8247V15.833C8.32565 16.2913 8.70065 16.6663 9.16732 16.6663H10.8423C11.3007 16.6663 11.684 16.2913 11.684 15.833V10.8163C11.684 10.8163 16.259 4.96634 16.4757 4.69967C16.6923 4.43301 16.6673 4.16634 16.6673 4.16634C16.6673 3.70801 16.2923 3.33301 15.8257 3.33301H4.17565C3.66732 3.33301 3.33398 3.73301 3.33398 4.16634C3.33398 4.33301 3.38398 4.53301 3.54232 4.71634Z" />
    </svg>
  );
};

const NavigationIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 20 20" {...props}>
      <g transform="translate(2,2) scale(0.8)">
        <path d="M15.8333 15.8333H4.16667V4.16667H10V2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H15.8333C16.75 17.5 17.5 16.75 17.5 15.8333V10H15.8333V15.8333ZM11.6667 2.5V4.16667H14.6583L6.46667 12.3583L7.64167 13.5333L15.8333 5.34167V8.33333H17.5V2.5H11.6667Z" />
      </g>
    </svg>
  );
};

const CloseIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 18 18" {...props}>
      <path d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z" />
    </svg>
  );
};
